import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService, attendanceService, leaveService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Briefcase,
  Timer,
  Award,
  ArrowRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Link } from 'react-router-dom';

const COLORS = ['#4CAF50', '#66BB6A', '#FFEB3B', '#FF5722', '#81C784'];

const EMPTY_EMPLOYEE_STATS = {
  total_working_days: 0,
  present_days: 0,
  absent_days: 0,
  late_days: 0,
  punctuality_score: 100,
  pending_requests: 0,
  leave_balance: [
    { leave_type: 'earned', total: 0, used: 0, available: 0 },
    { leave_type: 'sick', total: 0, used: 0, available: 0 },
    { leave_type: 'comp_off', total: 0, used: 0, available: 0 },
  ],
};

const EMPTY_LEAVE_STATUS = {
  pending: 0,
  approved: 0,
  rejected: 0,
  cancelled: 0,
  total: 0,
};

const summarizeLeaveStatus = (leaves = []) => {
  const summary = { ...EMPTY_LEAVE_STATUS };
  leaves.forEach((leave) => {
    const status = String(leave?.status || '').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(summary, status)) {
      summary[status] += 1;
    }
    summary.total += 1;
  });
  return summary;
};

const mapMyBalanceToDashboardBalance = (balance = {}) => {
  const leaveTypes = ['earned', 'sick', 'comp_off'];
  return leaveTypes.map((type) => ({
    leave_type: type,
    total: Number(balance?.[type]?.total || 0),
    used: Number(balance?.[type]?.used || 0),
    available: Number(balance?.[type]?.available || 0),
  }));
};

const buildFallbackEmployeeStats = (attendances = [], myBalance = {}, leaveStatus = EMPTY_LEAVE_STATUS) => {
  const presentStatuses = new Set(['checked_in', 'checked_out']);
  const totalWorkingDays = attendances.length;
  const presentDays = attendances.filter((item) => presentStatuses.has(item?.status)).length;
  const lateDays = attendances.filter((item) => Boolean(item?.is_late)).length;
  const absentDays = Math.max(totalWorkingDays - presentDays, 0);
  const onTimeDays = Math.max(presentDays - lateDays, 0);
  const punctualityScore = totalWorkingDays > 0
    ? Math.round((onTimeDays / totalWorkingDays) * 100)
    : 100;

  return {
    ...EMPTY_EMPLOYEE_STATS,
    total_working_days: totalWorkingDays,
    present_days: presentDays,
    absent_days: absentDays,
    late_days: lateDays,
    punctuality_score: punctualityScore,
    pending_requests: leaveStatus.pending,
    leave_balance: mapMyBalanceToDashboardBalance(myBalance),
  };
};

export default function DashboardPage() {
  const { user, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(EMPTY_EMPLOYEE_STATS);
  const [hasEmployeeStats, setHasEmployeeStats] = useState(false);
  const [leaveStatus, setLeaveStatus] = useState(EMPTY_LEAVE_STATUS);
  const [trends, setTrends] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const hasAdminView = isAdmin() || isManager();

      if (hasAdminView) {
        setHasEmployeeStats(false);
        const [statsData, trendsData, deptStats] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getAttendanceTrends(14),
          isAdmin() ? dashboardService.getDepartmentStats() : Promise.resolve([]),
        ]);
        setStats(statsData);
        setTrends(trendsData);
        setDepartmentStats(deptStats);
        // Admin/HR users may or may not have employee profile; ignore 404 for these roles.
        try {
          const empStats = await dashboardService.getEmployeeStats();
          if (empStats) {
            setEmployeeStats({
              ...EMPTY_EMPLOYEE_STATS,
              ...empStats,
              leave_balance: Array.isArray(empStats.leave_balance) && empStats.leave_balance.length > 0
                ? empStats.leave_balance
                : EMPTY_EMPLOYEE_STATS.leave_balance,
            });
            setHasEmployeeStats(true);
          }
        } catch (empError) {
          setHasEmployeeStats(false);
          if (!(isAdmin() && empError.response?.status === 404)) {
            console.error('Failed to load employee stats:', empError);
          }
        }
      } else {
        const [empStatsResult, myLeavesResult] = await Promise.allSettled([
          dashboardService.getEmployeeStats(),
          leaveService.getMyLeaves(),
        ]);

        const myLeaves = myLeavesResult.status === 'fulfilled' ? myLeavesResult.value : [];
        const statusSummary = summarizeLeaveStatus(myLeaves);
        setLeaveStatus(statusSummary);

        if (empStatsResult.status === 'fulfilled' && empStatsResult.value) {
          const empStats = empStatsResult.value;
          setEmployeeStats({
            ...EMPTY_EMPLOYEE_STATS,
            ...empStats,
            pending_requests: typeof empStats.pending_requests === 'number'
              ? empStats.pending_requests
              : statusSummary.pending,
            leave_balance: Array.isArray(empStats.leave_balance) && empStats.leave_balance.length > 0
              ? empStats.leave_balance
              : EMPTY_EMPLOYEE_STATS.leave_balance,
          });
          setHasEmployeeStats(true);
        } else {
          console.error('Failed to load employee stats:', empStatsResult.reason);
          const today = new Date();
          const month = today.getMonth() + 1;
          const year = today.getFullYear();
          const [attendanceResult, balanceResult] = await Promise.allSettled([
            attendanceService.getMyAttendance(month, year),
            leaveService.getMyBalance(),
          ]);

          const attendanceRows = attendanceResult.status === 'fulfilled' ? attendanceResult.value : [];
          const myBalance = balanceResult.status === 'fulfilled' ? balanceResult.value : {};
          setEmployeeStats(buildFallbackEmployeeStats(attendanceRows, myBalance, statusSummary));
          setHasEmployeeStats(true);
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isManager]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Admin/Manager Dashboard
  if (isAdmin() || isManager()) {
    return (
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Overview of your organization's attendance and leave management
            </p>
          </div>
          <Button asChild>
            <Link to="/dashboard/reports" data-testid="view-reports-btn">
              View Reports
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid" data-testid="admin-stats">
          <StatCard
            title="Total Employees"
            value={stats?.total_employees || 0}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Present Today"
            value={stats?.present_today || 0}
            icon={UserCheck}
            color="green"
            subtext={stats?.total_employees ? `${Math.round((stats.present_today / stats.total_employees) * 100)}%` : '0%'}
          />
          <StatCard
            title="Absent Today"
            value={stats?.absent_today || 0}
            icon={UserX}
            color="red"
          />
          <StatCard
            title="On Leave"
            value={stats?.on_leave_today || 0}
            icon={Calendar}
            color="yellow"
          />
        </div>

        {/* Pending Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="card-hover" data-testid="pending-leaves-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Pending Leave Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-500">
                {stats?.pending_leave_requests || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Awaiting your approval
              </p>
              <Button variant="outline" size="sm" className="mt-4" asChild>
                <Link to="/dashboard/leaves">Review Now</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="late-arrivals-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Late Arrivals Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-500">
                {stats?.late_arrivals_today || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Employees arrived late
              </p>
              <Button variant="outline" size="sm" className="mt-4" asChild>
                <Link to="/dashboard/attendance">View Details</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Attendance Trends */}
          <Card data-testid="attendance-trends-chart">
            <CardHeader>
              <CardTitle>Attendance Trends</CardTitle>
              <CardDescription>Last 14 days overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trends}>
                    <defs>
                      <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#4CAF50" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8F5E9" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#66BB6A"
                      fontSize={12}
                      tickFormatter={(value) => value.slice(5)}
                    />
                    <YAxis stroke="#66BB6A" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#ffffff', 
                        border: '1px solid #E8F5E9',
                        borderRadius: '8px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="present"
                      stroke="#4CAF50"
                      fillOpacity={1}
                      fill="url(#colorPresent)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Department Stats */}
          {isAdmin() && departmentStats.length > 0 && (
            <Card data-testid="department-stats-chart">
              <CardHeader>
                <CardTitle>Department Attendance</CardTitle>
                <CardDescription>Today's attendance by department</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={departmentStats} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8F5E9" />
                      <XAxis type="number" stroke="#66BB6A" fontSize={12} />
                      <YAxis 
                        dataKey="department" 
                        type="category" 
                        stroke="#66BB6A" 
                        fontSize={12}
                        width={100}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#ffffff', 
                          border: '1px solid #E8F5E9',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="present" fill="#4CAF50" name="Present" />
                      <Bar dataKey="absent" fill="#FF5722" name="Absent" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Employee Personal Stats */}
        {hasEmployeeStats && (
          <EmployeeStatsSection stats={employeeStats} leaveStatus={leaveStatus} />
        )}
      </div>
    );
  }

  // Employee Dashboard
  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Track your attendance and leave balance
        </p>
      </div>

      <EmployeeStatsSection stats={employeeStats} leaveStatus={leaveStatus} />
    </div>
  );
}

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    green: 'bg-green-500/10 text-green-500 border-green-500/20',
    red: 'bg-red-500/10 text-red-500 border-red-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  };

  return (
    <Card className="card-hover">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-3xl font-bold">{value}</span>
              {subtext && (
                <span className="text-sm text-muted-foreground">{subtext}</span>
              )}
            </div>
          </div>
          <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Employee Stats Section
const EmployeeStatsSection = ({ stats = EMPTY_EMPLOYEE_STATS, leaveStatus = EMPTY_LEAVE_STATUS }) => {
  const attendancePercentage = stats.total_working_days > 0
    ? Math.round((stats.present_days / stats.total_working_days) * 100)
    : 0;

  return (
    <>
      {/* My Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="employee-stats">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Present Days</p>
                <p className="text-2xl font-bold">{stats.present_days}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-500/10 text-red-500">
                <XCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Absent Days</p>
                <p className="text-2xl font-bold">{stats.absent_days}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/10 text-yellow-500">
                <Timer className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Late Days</p>
                <p className="text-2xl font-bold">{stats.late_days}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Punctuality Score</p>
                <p className="text-2xl font-bold">{stats.punctuality_score}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Progress */}
      <Card data-testid="attendance-progress-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>This Month's Attendance</span>
            <span className="text-primary">{attendancePercentage}%</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={attendancePercentage} className="h-3" />
          <p className="text-sm text-muted-foreground mt-2">
            {stats.present_days} present out of {stats.total_working_days} working days
          </p>
        </CardContent>
      </Card>

      {/* Leave Balance */}
      <Card data-testid="leave-balance-card">
        <CardHeader>
          <CardTitle>Leave Balance</CardTitle>
          <CardDescription>Your available leaves for this year</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.leave_balance?.map((leave, index) => (
              <div 
                key={leave.leave_type}
                className="p-4 rounded-lg border border-border bg-secondary/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm font-medium capitalize">
                    {leave.leave_type.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{leave.available}</span>
                  <span className="text-sm text-muted-foreground">/ {leave.total}</span>
                </div>
                <Progress 
                  value={leave.total > 0 ? (leave.available / leave.total) * 100 : 0}
                  className="h-1 mt-2"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="leave-status-card">
        <CardHeader>
          <CardTitle>Leave Status</CardTitle>
          <CardDescription>Current status of your leave requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3 bg-secondary/30">
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-xl font-semibold">{leaveStatus.pending}</p>
            </div>
            <div className="rounded-lg border p-3 bg-secondary/30">
              <p className="text-xs text-muted-foreground">Approved</p>
              <p className="text-xl font-semibold">{leaveStatus.approved}</p>
            </div>
            <div className="rounded-lg border p-3 bg-secondary/30">
              <p className="text-xs text-muted-foreground">Rejected</p>
              <p className="text-xl font-semibold">{leaveStatus.rejected}</p>
            </div>
            <div className="rounded-lg border p-3 bg-secondary/30">
              <p className="text-xs text-muted-foreground">Cancelled</p>
              <p className="text-xl font-semibold">{leaveStatus.cancelled}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6 flex flex-col items-center text-center">
            <Clock className="w-12 h-12 text-primary mb-4" />
            <h3 className="font-semibold mb-2">Mark Attendance</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Check in or check out for today
            </p>
            <Button asChild>
              <Link to="/dashboard/attendance" data-testid="mark-attendance-btn">
                Go to Attendance
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6 flex flex-col items-center text-center">
            <Calendar className="w-12 h-12 text-green-500 mb-4" />
            <h3 className="font-semibold mb-2">Apply Leave</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Request time off from work
            </p>
            <Button variant="outline" asChild>
              <Link to="/dashboard/leaves" data-testid="apply-leave-btn">
                Apply Now
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6 flex flex-col items-center text-center">
            <Briefcase className="w-12 h-12 text-yellow-500 mb-4" />
            <h3 className="font-semibold mb-2">Pending Requests</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {stats.pending_requests || 0} requests awaiting action
            </p>
            <Button variant="outline" asChild>
              <Link to="/dashboard/leaves">View Status</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
